import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { siteInfo } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    formation: "",
    situation: "",
    message: "",
  });

  const update = (k) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      setSubmitted(true);
      toast({
        title: "Demande envoyée",
        description: "Un conseiller CFMPE vous recontactera sous 48h.",
      });
      setTimeout(() => {
        setSubmitted(false);
        setForm({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          formation: "",
          situation: "",
          message: "",
        });
      }, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast({
        title: "Envoi impossible",
        description: `Merci de nous contacter au ${siteInfo.phone}. (${detail})`,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#f5f1e8]/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">Contact</span>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#0e3a3f] leading-[1.05] mt-3 title-underline">
              Discutons de votre projet.
            </h2>
            <p className="text-[#0e3a3f]/75 leading-relaxed mt-6 mb-10">
              Une question sur la formation, le financement ou l'inscription ?
              Notre équipe vous répond sous 48h ouvrées.
            </p>

            <div className="space-y-5">
              <a href={`tel:${siteInfo.phone.replace(/\./g,"")}`} className="flex items-start gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white border border-[#e6ded0] flex items-center justify-center group-hover:border-[#2c7a80] group-hover:bg-[#2c7a80] transition-colors">
                  <Phone className="w-5 h-5 text-[#2c7a80] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#0e3a3f]/50 font-medium">Téléphone</div>
                  <div className="font-display text-xl font-medium text-[#0e3a3f]">{siteInfo.phone}</div>
                  <div className="text-xs text-[#0e3a3f]/60 mt-0.5">Lun-Ven 9h-17h</div>
                </div>
              </a>
              <a href={`mailto:${siteInfo.email}`} className="flex items-start gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white border border-[#e6ded0] flex items-center justify-center group-hover:border-[#2c7a80] group-hover:bg-[#2c7a80] transition-colors">
                  <Mail className="w-5 h-5 text-[#2c7a80] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#0e3a3f]/50 font-medium">Email</div>
                  <div className="font-medium text-[#0e3a3f]">{siteInfo.email}</div>
                  <div className="text-xs text-[#0e3a3f]/60 mt-0.5">Réponse sous 48h</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white border border-[#e6ded0] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#2c7a80]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#0e3a3f]/50 font-medium">Adresse</div>
                  <div className="font-medium text-[#0e3a3f]">10 Avenue Georges Clemenceau</div>
                  <div className="text-sm text-[#0e3a3f]/70">06000 Nice, France</div>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#e6ded0] aspect-video">
              <iframe
                title="CFMPE Nice"
                src="https://www.openstreetmap.org/export/embed.html?bbox=7.253%2C43.700%2C7.283%2C43.710&layer=mapnik&marker=43.705%2C7.268"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-white rounded-3xl border border-[#e6ded0] p-8 lg:p-10 shadow-xl shadow-[#0e3a3f]/5"
            >
              {submitted ? (
                <div className="flex flex-col items-center text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-[#2c7a80]/10 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-[#2c7a80]" />
                  </div>
                  <h3 className="font-display text-3xl font-medium text-[#0e3a3f] mb-3">Merci pour votre envoi !</h3>
                  <p className="text-[#0e3a3f]/70 max-w-md">
                    Un conseiller CFMPE vous contactera sous 48h ouvrées pour étudier votre projet ensemble.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-medium text-[#0e3a3f] mb-2">Demande d'information</h3>
                  <p className="text-sm text-[#0e3a3f]/60 mb-6">Champs marqués d'un * requis</p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="firstName" className="text-sm mb-1.5 block">Prénom *</Label>
                      <Input
                        id="firstName"
                        required
                        value={form.firstName}
                        onChange={update("firstName")}
                        className="h-11 border-[#e6ded0] focus-visible:ring-[#2c7a80]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-sm mb-1.5 block">Nom *</Label>
                      <Input
                        id="lastName"
                        required
                        value={form.lastName}
                        onChange={update("lastName")}
                        className="h-11 border-[#e6ded0] focus-visible:ring-[#2c7a80]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="email" className="text-sm mb-1.5 block">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className="h-11 border-[#e6ded0] focus-visible:ring-[#2c7a80]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm mb-1.5 block">Téléphone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        className="h-11 border-[#e6ded0] focus-visible:ring-[#2c7a80]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="formation" className="text-sm mb-1.5 block">Formation souhaitée</Label>
                      <Select value={form.formation} onValueChange={update("formation")}>
                        <SelectTrigger id="formation" className="h-11 border-[#e6ded0] focus:ring-[#2c7a80]">
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initiale">CAP AEPE — Initiale</SelectItem>
                          <SelectItem value="alternance">CAP AEPE — Alternance</SelectItem>
                          <SelectItem value="continue">CAP AEPE — Continue</SelectItem>
                          <SelectItem value="vae">VAE petite enfance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="situation" className="text-sm mb-1.5 block">Votre situation</Label>
                      <Select value={form.situation} onValueChange={update("situation")}>
                        <SelectTrigger id="situation" className="h-11 border-[#e6ded0] focus:ring-[#2c7a80]">
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="salarie">Salarié(e)</SelectItem>
                          <SelectItem value="demandeur">Demandeur d'emploi</SelectItem>
                          <SelectItem value="etudiant">Étudiant(e)</SelectItem>
                          <SelectItem value="reconversion">Reconversion</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="message" className="text-sm mb-1.5 block">Votre projet *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={4}
                      placeholder="Parlez-nous de votre projet, vos objectifs, vos questions..."
                      value={form.message}
                      onChange={update("message")}
                      className="border-[#e6ded0] focus-visible:ring-[#2c7a80] resize-none"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-xs text-[#0e3a3f]/50">
                      Vos données sont traitées conformément au RGPD.
                    </p>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="bg-[#2c7a80] hover:bg-[#245e63] text-white rounded-full px-8 h-12 btn-primary group"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Envoi…
                        </>
                      ) : (
                        <>
                          Envoyer ma demande
                          <Send className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
